import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Typography
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { deleteUser, getAllApprovedUsers, updateUser } from "api/userData";
import cancelIcon from "assets/cancel.png";
import { LoadingSpinner } from "components/LoadingSpinner";
import { getPaginationProps } from "components/PaginationProps";
import { SearchComponent } from "components/SearchComponent";
import { UserType } from "constants/common";
import { useEffect, useRef, useState } from "react";
import { User as TUser } from "types/props";
import classes from "./User.module.css";
import { AddCreditsForUserModal } from "modals/AddCreditsForUserModal/AddCreditsForUserModal";

interface DataType {
  key: string;
  name: string;
  priority: number;
  userType: string;
  roleType: string;
  email: string;
  org: string;
  credits: string;
  discount: string;
  approved: boolean;
  causeOfUse: string;
}

const priorityMapper: { [key: string]: number | string } = {
  free: 9,
  external: 9,
  paid: 5,
  discounted: 5,
  internal: "2/5",
  client: 5
};

export const User: React.FC = () => {
  const [modal1Open, setModal1Open] = useState(false);
  const [addCreditsModal, setAddCreditsModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tableData, setTableData] = useState<DataType[]>([]);

  const [selectedUser, setSelectedUser] = useState<
    Pick<
      DataType,
      "key" | "userType" | "org" | "discount" | "approved" | "roleType" | "email" | "name"
    > & {
      prevUserRole: string;
    }
  >({
    key: "",
    userType: "",
    org: "",
    discount: "0",
    approved: false,
    roleType: "",
    email: "",
    name: "",
    prevUserRole: ""
  });

  const selectedUserId = useRef("");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdateUserLoading, setUpdateUserLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const formRef = useRef<FormInstance | null>(null);
  const { data: user } = useGetIdentity<TUser>();

  const handleClick = (id: string, record: DataType): any => {
    selectedUserId.current = id;
    setDeleteModalOpen(true);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text) => {
        return (
          <Typography.Text
            style={{ width: "200px" }}
            ellipsis={{
              tooltip: true
            }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Marketing emails",
      dataIndex: "marketingEmailPreference",
      key: "marketingEmailPreference",
      render: (value) => {
        return value ? "Enabled" : "Disabled";
      }
    },
    {
      title: "Organization",
      dataIndex: "org",
      key: "org"
    },
    {
      title: "Credits",
      key: "credits",
      dataIndex: "credits"
    },
    {
      title: "Free Credits",
      key: "freeCredits",
      dataIndex: "freeCredits"
    },
    {
      title: "Discount",
      key: "discount",
      dataIndex: "discount",
      render: (value, record) => {
        return record.userType === "DISCOUNTED" ? value : 0;
      }
    },
    {
      title: "User Type",
      key: "userType",
      dataIndex: "userType",
      filters: [
        {
          text: "Paid",
          value: "PAID"
        },
        {
          text: "Free",
          value: "FREE"
        },
        {
          text: "Internal",
          value: "INTERNAL"
        },
        {
          text: "Discounted",
          value: "DISCOUNTED"
        }
      ],
      onFilter: (value, record) => record.userType === value
    },
    {
      title: "Priority",
      key: "priority",
      dataIndex: "priority",
      filters: [
        {
          text: "5",
          value: 5
        },
        {
          text: "9",
          value: 9
        },
        {
          text: "2/5",
          value: "2/5"
        }
      ],
      onFilter: (value, record) => record.priority === value
    },
    {
      title: "Intent of Use",
      key: "causeOfUse",
      dataIndex: "causeOfUse",
      filters: [
        {
          text: "PERSONAL",
          value: "PERSONAL"
        },
        {
          text: "PROFESSIONAL",
          value: "PROFESSIONAL"
        }
      ],
      onFilter: (value, record) =>
        record.causeOfUse.toLowerCase().includes(value.toString().toLowerCase())
    },
    {
      title: "Sign up date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date: Date) => {
        return new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      },
      sortDirections: ["descend", "ascend"],
      sortIcon: (sortOrder) => {
        return sortOrder.sortOrder === "descend" ? (
          <ArrowDownOutlined style={{ color: "#CECFD2" }} />
        ) : sortOrder.sortOrder === "ascend" ? (
          <ArrowUpOutlined style={{ color: "#CECFD2" }} />
        ) : (
          <></>
        );
      }
    },
    {
      title: "Action",
      key: "action",
      render: (record) => {
        return (
          <Flex>
            <Button
              type="text"
              onClick={() => {
                setSelectedUser({
                  key: record.key,
                  userType: record.userType,
                  org: record.org,
                  discount: record.userType === "DISCOUNTED" ? record.discount : 0,
                  approved: record.approved,
                  roleType: record?.roleType || "",
                  prevUserRole: record?.roleType,
                  email: record?.email,
                  name: record?.name
                });
                setModal1Open(true);
                setIsLoading(false);
              }}>
              <EditOutlined className={classes.EditButton} />
            </Button>
            <Button type="text" onClick={() => handleClick(record.key, record)}>
              <DeleteOutlined className={classes.DeleteButton} />
            </Button>
          </Flex>
        );
      }
    }
  ];

  const fetchUsersData = async () => {
    try {
      const res = await getAllApprovedUsers();
      const data = res.map((item: any) => ({
        ...item,
        key: item.id,
        name: `${item.firstName} ${item.lastName ?? ""}`,
        priority: priorityMapper[item.userType.toLowerCase()],
        userType: item.userType,
        email: item.email,
        org: item.organization,
        credits: item.credits,
        discount: item.discount,
        approved: item.approved,
        roleType: item?.role?.type
      }));

      setTableData(data);
    } catch (error) {
      // console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    await deleteUser(selectedUserId.current);
    await fetchUsersData();
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <div>
        <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
          All users
        </Typography.Title>

        <SearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search by name, email, or organization">
          <Button onClick={() => setAddCreditsModal(true)} style={{ border: "none" }}>
            Add Credits
          </Button>
        </SearchComponent>
        <Table
          columns={columns}
          dataSource={tableData.filter((user: any) => {
            return (
              user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              user.org?.toLowerCase().includes(searchQuery.toLowerCase())
            );
          })}
          style={{ marginTop: "30px" }}
          scroll={{ x: "max-content" }}
          pagination={getPaginationProps()}
          onChange={(_: any, fil: any, sorter) => {
            const { order, columnKey } = sorter as { order: string; columnKey: keyof DataType };
            if (order && columnKey) {
              const sortedData = tableData.sort((a, b) => {
                const date1 = new Date(a[columnKey] as string);
                const date2 = new Date(b[columnKey] as string);
                // sort based on dates
                if (order === "ascend") {
                  return date1.getTime() - date2.getTime();
                } else {
                  return date2.getTime() - date1.getTime();
                }
              });
              setTableData([...sortedData]);
            }
          }}
        />
        <ConfigProvider
          theme={{
            token: {
              colorBgBase: "#202440"
            },
            components: {
              Modal: {
                contentBg: "#202440",
                colorText: "#fff"
              }
            }
          }}>
          <Modal
            open={modal1Open}
            onOk={() => setModal1Open(false)}
            onCancel={() => setModal1Open(false)}
            footer={null}
            closeIcon={<img src={cancelIcon} alt="cancel-icon" style={{ padding: "5px" }} />}
            destroyOnClose>
            <Row>
              <h1 className={classes.ModalTitle}>Edit User</h1>
            </Row>
            <Form ref={formRef} layout="vertical" name="edit-user">
              <>
                <Form.Item label="Email">
                  <Input disabled value={selectedUser.email} />
                </Form.Item>

                <Form.Item label="Organization">
                  <Input disabled value={selectedUser.org} />
                </Form.Item>

                <Form.Item
                  label="User role"
                  name="user-role"
                  rules={[
                    {
                      warningOnly: true,
                      validator: (_, value) => {
                        if (selectedUser.prevUserRole === "admin" && value !== "admin") {
                          return Promise.reject("This user would no longer be an admin");
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <Select
                    value={selectedUser.roleType}
                    defaultValue={selectedUser.roleType}
                    className={classes.select}
                    onChange={(value) => {
                      setSelectedUser({
                        ...selectedUser,
                        roleType: value,
                        userType: value === "admin" ? UserType.INTERNAL : selectedUser.userType
                      });
                    }}>
                    <Select.Option value="external">User</Select.Option>
                    <Select.Option value="client">API User</Select.Option>
                    <Select.Option value="admin">Admin</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item label="User type">
                  <Select
                    disabled={selectedUser.roleType === "admin"}
                    className={classes.select}
                    value={selectedUser.userType}
                    onChange={(value) => {
                      setSelectedUser({
                        ...selectedUser,
                        userType: value
                      });
                    }}>
                    <Select.Option value="0" disabled>
                      Select an option
                    </Select.Option>
                    <Select.Option value="INTERNAL">Internal User</Select.Option>
                    <Select.Option value="FREE">Free User</Select.Option>
                    <Select.Option value="DISCOUNTED">Discounted User</Select.Option>
                    <Select.Option value="PAID">Paid User</Select.Option>
                  </Select>
                </Form.Item>
              </>
              {selectedUser.userType === "DISCOUNTED" && (
                <>
                  <Form.Item
                    label="Discount percentage"
                    name="discount"
                    normalize={(value) => (value?.toString() || "").replace(/[^0-9]/g, "")}
                    style={{ marginBottom: 4 }}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value && (Number(value) < 0 || Number(value) > 100)) {
                            return Promise.reject("Discount percentage must be between 0 and 100");
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}>
                    <Input
                      className={classes.name}
                      placeholder="0"
                      defaultValue={selectedUser.discount.toString()}
                      value={selectedUser.discount.toString()}
                      onChange={(e) => {
                        setSelectedUser({
                          ...selectedUser,
                          discount: e.target.value
                        });
                      }}
                    />
                  </Form.Item>
                  <Typography.Text>Please input amount in percentage</Typography.Text>
                </>
              )}
              <Row style={{ marginTop: "20px" }}>
                <Button
                  style={{
                    height: "44px",
                    width: "100%",
                    borderRadius: "8px",
                    border: "none",
                    fontFamily: "Titillium web",
                    fontSize: "16px",
                    fontWeight: "600",
                    lineHeight: "24px"
                  }}
                  loading={isUpdateUserLoading}
                  disabled={isUpdateUserLoading}
                  onClick={async () => {
                    try {
                      await formRef.current?.validateFields();
                    } catch (error) {
                      return;
                    }

                    setUpdateUserLoading(true);
                    await updateUser(selectedUser.key, {
                      userType: selectedUser.userType,
                      organization: selectedUser.org,
                      discount:
                        selectedUser.userType === "DISCOUNTED" ? Number(selectedUser.discount) : 0,
                      approved: selectedUser.approved,
                      roleType: selectedUser.roleType
                    });
                    await fetchUsersData();
                    setUpdateUserLoading(false);
                    setModal1Open(false);
                  }}>
                  Confirm
                </Button>
              </Row>
            </Form>
          </Modal>
          <Modal
            open={deleteModalOpen}
            closable={false}
            maskClosable
            okText="Yes"
            cancelText="No"
            okButtonProps={{
              disabled: isLoading,
              loading: isLoading
            }}
            onCancel={() => setDeleteModalOpen(false)}
            onOk={handleDelete}
            cancelButtonProps={{
              style: {
                border: "none"
              }
            }}
            width={300}
            styles={{
              footer: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              },
              content: {
                padding: "12px"
              }
            }}>
            <Row className={classes.logoutTitle} justify="center">
              Delete User
            </Row>
            <Row className={classes.subTitle}>Are you sure you want to delete this user?</Row>
          </Modal>
        </ConfigProvider>
        <ConfigProvider
          theme={{
            token: {
              colorBgBase: "#202440"
            },
            components: {
              Modal: {
                contentBg: "#202440",
                colorText: "#fff"
              },
              InputNumber: {
                colorText: "white",
                colorIcon: "white",
                addonBg: "#0D111C",
                colorPrimary: "#fff"
              },
              Select: {
                colorText: "#fff"
              }
            }
          }}>
          {addCreditsModal && (
            <AddCreditsForUserModal
              users={tableData as any}
              onClose={() => setAddCreditsModal(false)}
              onSuccess={async () => {
                await fetchUsersData();
              }}
            />
          )}
        </ConfigProvider>
      </div>
    </>
  );
};
